<template>
  <div class="px-5 md:px-0 md:w-95 mx-auto max-w-max pb-12">
    <div v-if="activeTab === 1">
      <p class="text-center text-3xl font-semibold mt-8 tracking-tighter lg:tracking-normal">
        Sourcing Request Form
      </p>
      <p class="text-center font-normal text-lg lg:text-xl">
        Tell us about your product sourcing request by completing this Sourcing 
        Request Order form. <br />We will match your order to a Sourcing Expert within 
        2-3 working days. <br class="lg:hidden"/>After the match is confirmed, your Sourcing 
        Expert will get started on the project. 
      </p>
    </div>
    <div class="sourcing-stepper" v-if="activeTab < 6">
      <hr class="sourcing-stepper-break">
      <div
        v-for="(tab, index) in tabs"
        :key="tab"
        :class="activeTab === index+1 ? 'text-blue' : 'opacity-25'"
      >
        <p
          class="bg-black md:h-10 md:w-10 h-5 w-5 text-sm lg:text-2xl rounded-full text-white flex justify-center items-center mx-auto cursor-pointer"
          :class="{'bg-blue': activeTab === index+1}"
          @click="switchTab(index+1)"
        >
          {{ index + 1 }}
        </p>
        <p class="hidden lg:block text-xl font-semibold opacity-100 mt-6">
          {{ tab }} 
        </p>
      </div>
    </div>
    <CompanyDetails 
      v-if="activeTab === 1" 
      :userData="userData.companyData" 
      :editSourcingDetails="editSourcingDetails"
      @proceedNext="proceedNext"
      @updateSourcingForm="updateSourcingForm"
      @changeTab="changeTab"
    />
    <ProductDetails 
      v-else-if="activeTab === 2" 
      :userData="userData.productData" 
      @proceedNext="proceedNext" 
      @changeTab="changeTab"
      @updateSourcingForm="updateSourcingForm"
    />
    <SupplierCriteria 
      v-else-if="activeTab === 3" 
      :userData="userData.supplierData" 
      @proceedNext="proceedNext" 
      @changeTab="changeTab"
      @updateSourcingForm="updateSourcingForm"
    />
    <Payment 
      v-else-if="activeTab === 4" 
      :userData="userData.paymentData" 
      @proceedNext="proceedNext" 
      @changeTab="changeTab"
      @updateSourcingForm="updateSourcingForm"
      @getSummaryRecord="getSummaryRecord"
    />
    <Summary 
      v-else-if="activeTab === 5" 
      :summaryRecordsProp="summaryRecords"
      @proceedNext="proceedNext"
      @changeTab="changeTab"
      @editDetails="editDetails"
      @setReferenceNumber="setReferenceNumber"
    />
    <OrderConfirmation
      v-else-if="activeTab === 6"
      :reference_number="reference_number"
    /> 
  </div>
</template>
<script>

import { mapActions, mapMutations, mapGetters } from 'vuex'
import Payment from "@/shared/components/sourcing/Payment.vue";
import CompanyDetails from "@/shared/components/sourcing/CompanyDetailsForm.vue";
import ProductDetails from "@/shared/components/sourcing/ProductDetailsForm.vue";
import SupplierCriteria from "@/shared/components/sourcing/SupplierCriteriaForm.vue";
import Summary from '@/shared/components/sourcing/Summary.vue'
import OrderConfirmation from '@/shared/components/sourcing/OrderConfirmation.vue'

export default {
  name: "SourcingRequest",
  components: {
    CompanyDetails,
    Payment,
    ProductDetails,
    SupplierCriteria,
    Summary,
    OrderConfirmation
  },
  data() {
    return {
      activeTab: 1,
      tabs: ['Company Details' , 'Product Details' , 'Supplier Criteria' , 'Payment' ,'Summary'],
      userData: {
        companyData: null,
        productData: null,
        supplierData: null,
        paymentData: null,
      },
      
      editSourcingDetails: false,
      summaryRecords: {},
      reference_number: null,
    }
  },
  watch: {
    isUserLoggedIn() {
      this.fillDefaultCompanyData()
    },
    activeTab() {
      if(this.$route.query.utm_content != `step_${this.activeTab}`) {
        this.$router.replace({ name: "Sourcing Request", query: {utm_content: `step_${this.activeTab}`} })
      }
    }
  },
  mounted() {
    if(this.getCurrentTab != null) {
      this.activeTab = this.getCurrentTab
    }
  },
  created () {
    if (this.getSourcingToken) {
      if(localStorage.getItem('activeTab') !== null) {
        localStorage.removeItem('activeTab')
        localStorage.removeItem('previousUrl')
        this.linkClientToSourcingRequest({ _method: 'PUT' })
        localStorage.removeItem('existingSourcingToken')
        this.activeTab = 3;
      }
      this.setDraftData()
    } else {
      if (this.$route.query.editing) this.$router.push({ query: null })
      this.fillDefaultCompanyData()
    }
    this.setQueryParams()
  },
  beforeDestroy () {
    // this.setSourcingRequestToken(null)
  },
  computed: {
    ...mapGetters([
      'isUserLoggedIn',
      'getCurrentTab',
      'getUserData',
      'getUserAuthInfo',
      'getSourcingToken',
      'getOrderSummary'
    ])
  },

  methods: {
    ...mapActions([
      'fetchSourcingRequestSummary',
      'getSourcingOrderDraftInformation',
      'fetchBuyerAccount',
      'linkClientToSourcingRequest'
    ]),
    ...mapMutations ([
      'setSourcingRequestToken',
      'setCurrentTab',
      'setSummaryRecords',
      'setUserData'
    ]),
    changeTab() {
      this.activeTab = this.activeTab - 1;
      this.editSourcingDetails = true
      this.setCurrentTab(this.activeTab)
    },
    async setDraftData() {
      let userData = this.getUserData;
      if (userData) {
        this.editSourcingDetails = true
        this.userData.companyData = userData.companyData
        this.userData.productData = userData.productData
        this.userData.supplierData = userData.supplierData
        this.userData.paymentData = userData.paymentData
      }
    },
    async fillDefaultCompanyData() {
      if (this.isUserLoggedIn && !this.$route.query.editing && !this.userData.companyData && this.getUserAuthInfo.role === 'buyer') {
        const buyerInfo = await this.fetchBuyerAccount()
        this.userData.companyData = {
          first_name: buyerInfo.first_name,
          last_name: buyerInfo.last_name,
          email: buyerInfo.email,
          company_name: buyerInfo.company_name
        }
      }
    },
    updateSourcingForm(formData) {
      this.userData = { ...this.userData, ...formData },
      this.setUserData(formData)
    },
    setQueryParams() {
      const query = this.activeTab === 6 ? null : { ...this.$route.query, utm_content: `step_${this.activeTab}` }
      this.$router.push({ query })
    },
    switchTab(index) {
      // alert(index)
      if(this.activeTab > index) {
        this.activeTab = index;
        this.setCurrentTab(this.activeTab)
      } else if ( this.userData.paymentData != null ) {
        this.activeTab = index;
        this.setCurrentTab(this.activeTab)
      } else if ( this.userData.productData != null && index <= 3) {
        this.activeTab = index;
        this.setCurrentTab(this.activeTab)
      } 
    },
    async proceedNext() {
      this.activeTab += 1
      this.setCurrentTab(this.activeTab)
      window.scrollTo(0, 0)
      this.setQueryParams()
    },
    async editDetails() {
      await this.setDraftData()
      this.editSourcingDetails = true
      this.activeTab = 1
      window.scrollTo(0, 0)
      this.setQueryParams()
    }
    ,
    getSummaryRecord(val){
      if (val) {
        const { order_summary } = val
        this.summaryRecords = order_summary
        this.setSummaryRecords(order_summary)
      }
    },
    setReferenceNumber (val) {
       if (val) {
        this.reference_number = val
      }
    }
  },
};
</script>
<style lang="scss" scoped>
.sourcing-stepper {
  @apply relative flex justify-between items-center;
  padding-top: 3rem;
  padding-bottom: 3rem;
  @screen lg {
    padding-top: 1.75rem;
    padding-bottom: 2rem;
  }
  &-break {
    @apply block absolute opacity-75 w-full;
    z-index: -1;
    @screen lg {
      width: auto;
      top: 3rem;
      left: 3.75rem;
      right: 3.75rem;
    }
  }
}
// .de-select-tab{
//   // background-color: #e2e8f0;
// }
</style>
